import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Database, Wrench, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { triggerApi } from '../../services/triggerApi';
import {
  TriggerComplianceReport,
  TenantTriggerStatus,
  TriggerConfiguration
} from '../../types/TriggerCompliance';

export const TriggerCompliance: React.FC = () => {
  const [report, setReport] = useState<TriggerComplianceReport | null>(null);
  const [configurations, setConfigurations] = useState<TriggerConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState<number | 'all' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedTenants, setExpandedTenants] = useState<Set<number>>(new Set());
  const [showConfigurations, setShowConfigurations] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await triggerApi.checkAllTenants();
      setReport(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load compliance report');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConfigurations = useCallback(async () => {
    try {
      const data = await triggerApi.getConfigurations();
      setConfigurations(data);
    } catch (err: any) {
      console.error('Failed to load configurations:', err);
    }
  }, []);

  useEffect(() => {
    loadReport();
    loadConfigurations();
  }, [loadReport, loadConfigurations]);

  const handleFixTenant = async (tenantId: number) => {
    setFixing(tenantId);
    try {
      await triggerApi.fixTenant(tenantId);
      await loadReport();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to fix tenant triggers');
    } finally {
      setFixing(null);
    }
  };

  const handleFixAll = async () => {
    setFixing('all');
    try {
      await triggerApi.fixAllTenants();
      await loadReport();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to fix all triggers');
    } finally {
      setFixing(null);
    }
  };

  const toggleExpanded = (tenantId: number) => {
    const newExpanded = new Set(expandedTenants);
    if (newExpanded.has(tenantId)) {
      newExpanded.delete(tenantId);
    } else {
      newExpanded.add(tenantId);
    }
    setExpandedTenants(newExpanded);
  };

  const getStatusIcon = (tenant: TenantTriggerStatus) => {
    if (!tenant.databaseExists) {
      return <Database className="w-5 h-5 text-gray-400" />;
    }
    if (tenant.isFullyCompliant) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  const getStatusBadge = (tenant: TenantTriggerStatus) => {
    if (!tenant.databaseExists) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Ingen database
        </span>
      );
    }
    if (tenant.isFullyCompliant) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          OK
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        {tenant.nonCompliantTriggers} avvik
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Trigger Compliance</h2>
          <p className="text-sm text-gray-500">
            Sjekk og fiks trigger-status p&aring; alle tenant-databaser
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowConfigurations(!showConfigurations)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Settings className="w-4 h-4 mr-2" />
            Konfigurasjon
          </button>
          <button
            onClick={loadReport}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Oppdater
          </button>
          <button
            onClick={handleFixAll}
            disabled={fixing !== null || loading}
            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <Wrench className={`w-4 h-4 mr-2 ${fixing === 'all' ? 'animate-spin' : ''}`} />
            Fiks alle
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Configuration panel */}
      {showConfigurations && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Triggere som skal v&aelig;re disabled</h3>
          <div className="max-h-64 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tabell</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Beskrivelse</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {configurations.map((config) => (
                  <tr key={config.id}>
                    <td className="px-3 py-2 text-sm font-mono text-gray-900">{config.triggerName}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">{config.tableName}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">{config.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary cards */}
      {report && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-500">Totalt tenanter</div>
            <div className="text-2xl font-semibold text-gray-900">{report.totalTenants}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-500">OK</div>
            <div className="text-2xl font-semibold text-green-600">{report.compliantTenants}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-500">Avvik</div>
            <div className="text-2xl font-semibold text-red-600">{report.nonCompliantTenants}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-500">Uten database</div>
            <div className="text-2xl font-semibold text-gray-400">{report.tenantsWithoutDatabase}</div>
          </div>
        </div>
      )}

      {/* Tenant list */}
      {loading && !report && (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 mx-auto text-gray-400 animate-spin" />
          <p className="mt-2 text-sm text-gray-500">Sjekker trigger-status...</p>
        </div>
      )}

      {report && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Database</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Compliance / Totalt</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {report.tenants.map((tenant) => (
                <React.Fragment key={tenant.tenantId}>
                  <tr className={`hover:bg-gray-50 ${!tenant.databaseExists ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-4">
                      {tenant.databaseExists && tenant.triggers.length > 0 && (
                        <button
                          onClick={() => toggleExpanded(tenant.tenantId)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {expandedTenants.has(tenant.tenantId) ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(tenant)}
                        <span className="ml-2">{getStatusBadge(tenant)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{tenant.tenantName}</div>
                      <div className="text-sm text-gray-500">ID: {tenant.tenantId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tenant.databaseName}</div>
                      <div className="text-sm text-gray-500">{tenant.serverName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {tenant.databaseExists ? (
                        <div>
                          <div>{tenant.compliantTriggers}/{tenant.checkedTriggers} OK</div>
                          <div className="text-xs text-gray-400">Totalt: {tenant.totalTriggersInDatabase} triggere</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {tenant.databaseExists && !tenant.isFullyCompliant && (
                        <button
                          onClick={() => handleFixTenant(tenant.tenantId)}
                          disabled={fixing !== null}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-50"
                        >
                          <Wrench className={`w-3 h-3 mr-1 ${fixing === tenant.tenantId ? 'animate-spin' : ''}`} />
                          Fiks
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* Expanded trigger details */}
                  {expandedTenants.has(tenant.tenantId) && tenant.triggers.length > 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 bg-gray-50">
                        <div className="ml-8">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Trigger</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Tabell</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Finnes</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Disabled</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {tenant.triggers.map((trigger, idx) => (
                                <tr key={idx} className={trigger.isCompliant ? '' : 'bg-red-50'}>
                                  <td className="px-3 py-2 text-sm font-mono">{trigger.triggerName}</td>
                                  <td className="px-3 py-2 text-sm">{trigger.tableName}</td>
                                  <td className="px-3 py-2 text-sm">
                                    {trigger.triggerExists ? (
                                      <CheckCircle className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <span className="text-gray-400">Nei</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-sm">
                                    {trigger.isDisabled ? (
                                      <CheckCircle className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-red-500" />
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-sm">
                                    {trigger.isCompliant ? (
                                      <span className="text-green-600">OK</span>
                                    ) : (
                                      <span className="text-red-600">Aktivert (skal v&aelig;re disabled)</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Report timestamp */}
      {report && (
        <div className="text-sm text-gray-500 text-right">
          Sist oppdatert: {new Date(report.generatedAt).toLocaleString('nb-NO')}
        </div>
      )}
    </div>
  );
};

export default TriggerCompliance;
